import { Card, CardContent } from "@/components/ui/card";
import { ClicksInfoButton, CPDCInfoButton } from "@/components/ui/MetricInfo";
import { Eye, MousePointer, Target, DollarSign, Heart, Ticket, CheckCircle2 } from "lucide-react";

/**
 * Shared Metric Card component used by both Dashboard and Analytics pages
 * Ensures consistent styling and display across the application
 */

const metricConfig = {
  impressions: {
    label: "Views",
    sublabel: "Total Impressions",
    icon: Eye,
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-500",
    format: (value) => value?.toLocaleString() || "0"
  },
  clicks: {
    label: "Clicks",
    sublabel: null, // Will be replaced with CTR
    icon: MousePointer,
    iconBg: "bg-orange-500/10",
    iconColor: "text-orange-500",
    format: (value) => value?.toLocaleString() || "0",
    // Round 2 asked what a click is and how often one is charged (#17). Declared here
    // rather than added to each page, so Dashboard and Analytics cannot disagree.
    explainer: ClicksInfoButton,
  },
  cpdc: {
    label: "Delivered Customers",
    sublabel: "Customers who visited",
    icon: Target,
    iconBg: "bg-purple-500/10",
    iconColor: "text-purple-500",
    format: (value) => value?.toLocaleString() || "0",
    // This existed but was wired only into the campaign builder's pricing panel, so the
    // card a merchant reads their results from explained nothing.
    explainer: CPDCInfoButton,
  },
  spend: {
    label: "Total Spend",
    sublabel: "AUD",
    icon: DollarSign,
    iconBg: "bg-green-500/10",
    iconColor: "text-green-500",
    format: (value) => `$${value?.toLocaleString() || "0"}`
  },
  favorites: {
    label: "Followers",
    sublabel: "Customers who have favourited your venue",
    icon: Heart,
    iconBg: "bg-pink-500/10",
    iconColor: "text-pink-500",
    format: (value) => value?.toLocaleString() || "0"
  },
  saved_offers: {
    label: "Saved Offers",
    sublabel: "Deals saved from in-car displays",
    icon: Ticket,
    iconBg: "bg-yellow-500/10",
    iconColor: "text-yellow-500",
    format: (value) => value?.toLocaleString() || "0"
  },
  redeems: {
    label: "Redeems",
    // Round 12, FP-25, Joel's wording. Mine said "Saved offers used at the venue" and
    // was wrong twice: an offer does not have to be SAVED to be redeemed - a customer
    // can open it in Discovery and redeem on the spot - and redemption may eventually
    // be online rather than at a physical venue.
    sublabel: "Offers redeemed at your business",
    icon: CheckCircle2,
    iconBg: "bg-green-500/10",
    iconColor: "text-green-500",
    format: (value) => value?.toLocaleString() || "0"
  }
};

export function MetricCard({
  type,
  value,
  ctr = null,  // For clicks card
  savedOffers = null, // Saved Offers shown nested under Clicks
  direct = null, // For cpdc breakdown
  proximity = null, // For cpdc breakdown (displayed as "Nearby Influenced")
  prompted = null, // For cpdc breakdown
  testId = null,
  className = ""
}) {
  const config = metricConfig[type];
  if (!config) return null;
  
  const Icon = config.icon;
  const Explainer = config.explainer || null;
  const hasCpdcBreakdown = type === 'cpdc' && (direct !== null || proximity !== null || prompted !== null);
  
  return (
    <Card className={`bg-card border-border ${className}`} data-testid={testId}>
      <CardContent className="p-5">
        {/* Round 12, FP-5: "Info button on dashboard should be in same places either
            next to word or far right not a different position for both".
            Both, previously. The button followed its label, and only two cards carry
            one: CLICKS is short and DELIVERED CUSTOMERS wraps, so one icon sat two
            words in and the other sat a line down and much further across. Anchored to
            the card's right edge instead, which is the only placement that does not
            move when a label's length does, and which matches the campaign pricing
            panels. */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`p-2.5 rounded-lg shrink-0 ${config.iconBg}`}>
              <Icon className={`h-6 w-6 ${config.iconColor}`} />
            </div>
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              {config.label}
            </p>
          </div>
          {Explainer && (
            <div className="shrink-0 self-start">
              <Explainer />
            </div>
          )}
        </div>
        <p className="text-3xl font-bold mt-3">
          {config.format(value)}
        </p>
        {type === 'clicks' ? (
          <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
            {ctr !== null && <p>CTR: {ctr}%</p>}
            {savedOffers !== null && <p>Saved Offers: {(savedOffers || 0).toLocaleString()}</p>}
          </div>
        ) : hasCpdcBreakdown ? (
          <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
            <p>Direct To Door: {(direct || 0).toLocaleString()}</p>
            <p>Nearby Influenced: {(proximity || 0).toLocaleString()}</p>
            {/* Prompted is no longer a meter at all (2026-09-03) - it bills and counts
                as Direct, so there is nothing left to fold or to explain. */}
          </div>
        ) : config.sublabel ? (
          <p className="text-xs text-muted-foreground mt-1">
            {config.sublabel}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Chart color constants - use these across all charts for consistency
 */
export const CHART_COLORS = {
  impressions: "#3B82F6",  // blue-500 (Views)
  clicks: "#F97316",       // orange-500 (Clicks)
  deals_saved: "#EAB308",  // yellow-500 (Deals Saved)
  cpdc: "#A855F7",         // purple-500 (Delivered Customers)
  spend: "#22C55E"         // green-500 (Total Spend)
};

/**
 * Gradient definitions for area charts
 */
export const getChartGradient = (id, color) => ({
  id,
  color,
  stops: [
    { offset: "5%", opacity: 0.3 },
    { offset: "95%", opacity: 0 }
  ]
});

export default MetricCard;
